import { LegacyRef, useMemo, type FC } from "react";
import { useWindowSize } from "libs/hooks/src";
import useIntersectionObserver from "libs/hooks/src/lib/useIntersectionObserver";

import { cn, makeBreakpointClassName } from "@/ui/lib/utils";
import {
  IMAGE_DEFAULTS,
  TImageProps,
  IMAGE_RESPONSIVE_FIT_CLASS_MAP,
} from "@/ui/components/primitives/Image";
import { ImageSrcSet } from "./ImageSrcSet";
import { useAspectRatio } from "@/ui/lib/hooks/useAspectRatio";
import {
  getActiveImageDimensions,
  getImageOpacityClasses,
  isDefaultFit,
} from "./Image.utils";

export const Image: FC<TImageProps> = (
  {
    src,
    width,
    height,
    alt,
    loading,
    srcSet,
    fit = IMAGE_DEFAULTS.fit,
    className,
    ...props
  },
) => {
  const { aspectRatioStyles } = useAspectRatio();
  const [ imageRef, isVisible ] = useIntersectionObserver();
  const { windowSize } = useWindowSize();

  const activeDimensions = useMemo(() => (
    getActiveImageDimensions(windowSize, srcSet, {
      width,
      height,
    })
  ), [ windowSize, width, height, srcSet ]);

  const imageOpacityClasses = useMemo(() => (
    getImageOpacityClasses(loading, isVisible)
  ), [ isVisible, activeDimensions, loading ]);

  return (
    <div
      data-component="Image"
      className={cn(
        "w-full",
      )}
      style={isDefaultFit(fit) ? aspectRatioStyles(activeDimensions.width, activeDimensions.height) : undefined}
    >
      <picture>
        <ImageSrcSet images={srcSet} />

        <img
          {...props}
          ref={imageRef as LegacyRef<HTMLImageElement>}
          src={src}
          alt={alt}
          height={activeDimensions.height}
          width={activeDimensions.width}
          loading={loading}
          className={cn(
            "block w-full transition-opacity duration-1000",
            imageOpacityClasses,
            makeBreakpointClassName(fit, IMAGE_RESPONSIVE_FIT_CLASS_MAP, IMAGE_DEFAULTS.fit),
            className,
          )}
        />
      </picture>
    </div>
  );
};

Image.displayName = "Image";
